import { api } from './api';

export type ScanDefinitionStatus = 'ACTIVE' | 'ARCHIVED';
export type ScheduleCadence = 'OFF' | 'DAILY' | 'WEEKLY';

export interface ScanDefinition {
  scan_definition_id: string;
  version: number;
  status: ScanDefinitionStatus;
  name: string;
  description?: string | null;
  scope?: Record<string, unknown>;
  engine_config?: Record<string, unknown>;
}

export interface ScanDefinitionScope {
  camera_ids?: string[];
  zone_ids?: string[];
  policy_ids?: string[];
  device_id?: string;
  policy_type?: string;
  incident_type?: string;
  [key: string]: unknown;
}

export interface ScanDefinitionWriteInput {
  name: string;
  description?: string | null;
  scope: ScanDefinitionScope;
  engine_config?: Record<string, unknown>;
}

export interface ScheduledScanRecipient {
  recipient_type: 'USER' | 'EMAIL';
  user_id?: string | null;
  email_address?: string | null;
  enabled?: boolean;
}

export interface ScheduledScanSchedule {
  id: string;
  scan_definition_id: string;
  version: number;
  enabled: boolean;
  cadence: ScheduleCadence;
  day_of_week?: number | null;
  execution_time_local: string;
  timezone: string;
  window_length_seconds: number;
  settlement_delay_seconds: number;
  retention_policy_id: string;
  materialized_through_utc?: string | null;
  recipients: ScheduledScanRecipient[];
  safety_case_id?: string | null;
  finding_id?: string | null;
}

export interface ScheduleWriteInput {
  expected_version?: number;
  enabled: boolean;
  cadence: ScheduleCadence;
  day_of_week: number | null;
  execution_time_local: string;
  timezone: string;
  window_length_seconds: number;
  settlement_delay_seconds: number;
  retention_policy_id: string;
  recipients: ScheduledScanRecipient[];
}

export interface ScheduledScanNotificationSummary {
  id: string;
  notification_type: string;
  status: 'PENDING' | 'SENT' | 'FAILED' | 'SUPPRESSED';
  recipient_count: number;
  sent_at?: string | null;
  failed_at?: string | null;
  failure_code?: string | null;
  retry_count: number;
}

export interface ScheduledScanRunLinks {
  pipeline_run_id?: string | null;
  coverage_result_id?: string | null;
  result_summary_id?: string | null;
}

export interface ScheduledScanRunConfiguration {
  scheduled_run_id: string;
  schedule_enabled: boolean;
  schedule_cadence: string;
  schedule_day_of_week?: number | null;
  schedule_execution_time_local: string;
  schedule_timezone: string;
  schedule_window_length_seconds: number;
  schedule_settlement_delay_seconds: number;
  schedule_retention_policy_id: string;
  application_version?: string | null;
}

export interface ScheduledScanRun {
  id: string;
  schedule_id: string;
  schedule_version: number;
  scan_definition_id: string;
  scan_definition_version: number;
  occurrence_at_local: string;
  occurrence_at_utc: string;
  window_start_utc: string;
  window_end_utc: string;
  status: string;
  idempotency_key: string;
  started_at?: string | null;
  completed_at?: string | null;
  terminal_reason_code?: string | null;
  terminal_reason_message?: string | null;
  retry_count: number;
  notification?: ScheduledScanNotificationSummary | null;
  links: ScheduledScanRunLinks;
  configuration?: ScheduledScanRunConfiguration | null;
}

export interface ScheduledScanStatusSummary {
  items: Array<{
    definition: ScanDefinition;
    schedule: ScheduledScanSchedule;
    runs: ScheduledScanRun[];
  }>;
  total: number;
  limit: number;
  offset: number;
  run_status_vocabulary: string[];
}

function sitePath(siteId: string): string {
  return `/api/v1/sites/${encodeURIComponent(siteId)}`;
}

export const scheduledScansApi = {
  async featureEnabled(): Promise<boolean> {
    const response = await api.get('/api/v1/feature-flags');
    return response.data?.flags?.scheduled_scans_enabled === true;
  },

  async listDefinitions(siteId: string): Promise<ScanDefinition[]> {
    const response = await api.get(`${sitePath(siteId)}/scan-definitions`);
    return response.data?.items || [];
  },

  async getSiteStatusSummary(siteId: string, limit = 100): Promise<ScheduledScanStatusSummary> {
    const boundedLimit = Math.max(1, Math.min(200, Math.floor(limit || 100)));
    const items: ScheduledScanStatusSummary['items'] = [];
    let total = 0;
    let offset = 0;
    let responseLimit = boundedLimit;
    let vocabulary: string[] = [];
    do {
      const response = await api.get(
        `${sitePath(siteId)}/scheduled-scan-status?limit=${boundedLimit}&offset=${offset}`,
      );
      const pageItems = response.data?.items || [];
      total = Number(response.data?.total || 0);
      responseLimit = Number(response.data?.limit || boundedLimit);
      vocabulary = response.data?.run_status_vocabulary || vocabulary;
      items.push(...pageItems);
      if (!pageItems.length) break;
      offset += pageItems.length;
    } while (items.length < total);
    return {
      items,
      total,
      limit: responseLimit,
      offset: 0,
      run_status_vocabulary: vocabulary,
    };
  },

  async createDefinition(
    siteId: string,
    input: ScanDefinitionWriteInput,
  ): Promise<ScanDefinition> {
    const response = await api.post(`${sitePath(siteId)}/scan-definitions`, input);
    return response.data.definition;
  },

  async reviseDefinition(
    siteId: string,
    definitionId: string,
    input: ScanDefinitionWriteInput & { expected_version: number },
  ): Promise<ScanDefinition> {
    const response = await api.patch(
      `${sitePath(siteId)}/scan-definitions/${encodeURIComponent(definitionId)}`,
      input,
    );
    return response.data.definition;
  },

  async listSchedules(siteId: string, definitionId: string): Promise<ScheduledScanSchedule[]> {
    const response = await api.get(
      `${sitePath(siteId)}/scan-definitions/${encodeURIComponent(definitionId)}/schedules`,
    );
    return response.data?.items || [];
  },

  async createSchedule(
    siteId: string,
    definitionId: string,
    input: ScheduleWriteInput,
  ): Promise<ScheduledScanSchedule> {
    const response = await api.post(
      `${sitePath(siteId)}/scan-definitions/${encodeURIComponent(definitionId)}/schedules`,
      input,
    );
    return response.data.schedule;
  },

  async updateSchedule(
    siteId: string,
    scheduleId: string,
    input: ScheduleWriteInput & { expected_version: number },
  ): Promise<ScheduledScanSchedule> {
    const response = await api.patch(
      `${sitePath(siteId)}/schedules/${encodeURIComponent(scheduleId)}`,
      input,
    );
    return response.data.schedule;
  },

  async listDefinitionRuns(siteId: string, definitionId: string): Promise<ScheduledScanRun[]> {
    const response = await api.get(
      `${sitePath(siteId)}/scan-definitions/${encodeURIComponent(definitionId)}/runs`,
    );
    return response.data?.items || [];
  },

  async getRun(siteId: string, runId: string): Promise<ScheduledScanRun> {
    const response = await api.get(
      `${sitePath(siteId)}/runs/${encodeURIComponent(runId)}`,
    );
    return response.data.run;
  },

  async cancelRun(siteId: string, runId: string, reason?: string): Promise<ScheduledScanRun> {
    const response = await api.post(
      `${sitePath(siteId)}/runs/${encodeURIComponent(runId)}/cancel`,
      reason ? { reason } : {},
    );
    return response.data.run;
  },

  async retryRun(siteId: string, runId: string, reason?: string): Promise<ScheduledScanRun> {
    const response = await api.post(
      `${sitePath(siteId)}/runs/${encodeURIComponent(runId)}/retry`,
      reason ? { reason } : {},
    );
    return response.data.run;
  },
};
